import { ACTIVITY_SESSION_TYPE } from "@/app/report/common/constants"
import { create } from "zustand"
import { devtools } from "zustand/middleware"
import type { LayoutItem } from "react-grid-layout"
import type {
  PdfBuilderStore,
  PdfBuilderState,
  Page,
  PageConfig,
  CanvasItem,
  PaletteItem,
  ReportKindSnapshot,
} from "@/types/pdfBuilder"
import { snapWidth } from "@/app/report/pdf/builder/constants/gridSnap"

const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

const defaultPageConfig: PageConfig = {
  size: "a4",
  orientation: "portrait",
  marginTop: 10,
  marginBottom: 10,
  marginLeft: 10,
  marginRight: 10,
}

const createDefaultPage = (config?: Partial<PageConfig>): Page => ({
  id: generateId(),
  name: undefined,
  items: [],
  config: { ...defaultPageConfig, ...config },
})

/** 기간 기본값 — 액티비티 리포트는 1년, 트렌드는 1개월 (2026-10-02 요청) */
export function defaultPeriod(kind: "daily" | "trend"): { start: string; end: string } {
  const end = new Date()
  const start = new Date()
  if (kind === "trend") {
    start.setMonth(start.getMonth() - 1)
  } else {
    start.setFullYear(start.getFullYear() - 1)
  }
  const format = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`

  return { start: format(start), end: format(end) }
}

const initialState = {
  pages: [createDefaultPage()],
  currentPageId: null as string | null,
  selectedItemId: null as string | null,
  isDraggingFromPalette: false,
  draggedPaletteItem: null as PaletteItem | null,
  isExporting: false,
  isInteracting: false,
  exportProgress: 0,
  headGaps: {} as Record<string, number>,
  headGapRows: {} as Record<string, number>,
  headGapBase: {} as Record<string, number>,
  filterActivityId: null as number | null,
  filterSessionIds: [] as number[],
  filterPlayerIds: [] as number[],
  filterReportMode: ACTIVITY_SESSION_TYPE.SESSION as ACTIVITY_SESSION_TYPE,
  filterReportTemplateId: null as number | null,
  filterReportKind: "daily" as "daily" | "trend",
  filterPeriod: defaultPeriod("daily"),
  kindSnapshots: {} as PdfBuilderState["kindSnapshots"],
}

export const usePdfBuilderStore = create<PdfBuilderStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      currentPageId: initialState.pages[0]?.id ?? null,

      addPage: (config) => {
        const newPage = createDefaultPage(config)
        set((state) => ({
          pages: [...state.pages, newPage],
          currentPageId: newPage.id,
        }))
      },

      removePage: (pageId) => {
        set((state) => {
          const newPages = state.pages.filter((p) => p.id !== pageId)
          if (newPages.length === 0) {
            const defaultPage = createDefaultPage()
            return {
              pages: [defaultPage],
              currentPageId: defaultPage.id,
              selectedItemId: null,
            }
          }
          const newCurrentPageId =
            state.currentPageId === pageId ? (newPages[0]?.id ?? null) : state.currentPageId
          return {
            pages: newPages,
            currentPageId: newCurrentPageId,
            selectedItemId: state.currentPageId === pageId ? null : state.selectedItemId,
          }
        })
      },

      reorderPages: (fromIndex, toIndex) => {
        set((state) => {
          const newPages = [...state.pages]
          const [removed] = newPages.splice(fromIndex, 1)
          newPages.splice(toIndex, 0, removed)
          return { pages: newPages }
        })
      },

      setCurrentPage: (pageId) => {
        set({ currentPageId: pageId, selectedItemId: null })
      },

      updatePageConfig: (pageId, config) => {
        set((state) => ({
          pages: state.pages.map((p) =>
            p.id === pageId ? { ...p, config: { ...p.config, ...config } } : p,
          ),
        }))
      },

      addItem: (pageId, item) => {
        const newItem: CanvasItem = {
          ...item,
          id: generateId(),
          w: snapWidth(item.w),
        }
        set((state) => ({
          pages: state.pages.map((p) =>
            p.id === pageId ? { ...p, items: [...p.items, newItem] } : p,
          ),
          selectedItemId: newItem.id,
        }))
      },

      removeItem: (pageId, itemId) => {
        set((state) => ({
          pages: state.pages.map((p) =>
            p.id === pageId ? { ...p, items: p.items.filter((i) => i.id !== itemId) } : p,
          ),
          selectedItemId: state.selectedItemId === itemId ? null : state.selectedItemId,
        }))
      },

      updateItem: (pageId, itemId, updates) => {
        set((state) => ({
          pages: state.pages.map((p) =>
            p.id === pageId
              ? {
                  ...p,
                  items: p.items.map((i) => (i.id === itemId ? { ...i, ...updates } : i)),
                }
              : p,
          ),
        }))
      },

      updateItemLayout: (pageId, itemId, layout) => {
        // 값이 그대로면 아무것도 하지 않는다 — pages 배열을 새로 만들면 구독자 전체가 깨어나고,
        // react-grid-layout 메아리(#179)·useMinItemRows 의 effect 재진입과 겹쳐 재렌더가 증폭된다 (#197)
        const target = get()
          .pages.find((p) => p.id === pageId)
          ?.items.find((i) => i.id === itemId)
        if (
          target &&
          (["x", "y", "w", "h", "minW", "minH", "maxW", "maxH"] as const).every(
            (k) => layout[k] === undefined || layout[k] === target[k],
          )
        ) {
          return
        }

        set((state) => ({
          pages: state.pages.map((p) =>
            p.id === pageId
              ? {
                  ...p,
                  items: p.items.map((i) =>
                    i.id === itemId
                      ? {
                          ...i,
                          x: layout.x ?? i.x,
                          y: layout.y ?? i.y,
                          w: layout.w ?? i.w,
                          h: layout.h ?? i.h,
                          // 최소·최대 크기도 받는다 — 표는 내용 높이를 최소 크기로 둔다 (2026-09-28)
                          minW: layout.minW ?? i.minW,
                          minH: layout.minH ?? i.minH,
                          maxW: layout.maxW ?? i.maxW,
                          maxH: layout.maxH ?? i.maxH,
                        }
                      : i,
                  ),
                }
              : p,
          ),
        }))
      },

      selectItem: (itemId) => {
        set({ selectedItemId: itemId })
      },

      setInteracting: (isInteracting) => {
        set((state) => (state.isInteracting === isInteracting ? state : { isInteracting }))
      },

      setReportKind: (kind) => {
        set((state) => {
          if (state.filterReportKind === kind) {
            return state
          }
          // 지금까지의 작업(배치·필터)은 그 리포트 것으로 넣어 두고, 돌아오면 그대로 되살린다 (2026-10-02 요청).
          // 쓸 수 있는 컴포넌트·템플릿이 다르므로 캔버스는 리포트마다 따로 가진다
          const kept: ReportKindSnapshot = {
            pages: state.pages,
            currentPageId: state.currentPageId,
            filterPeriod: state.filterPeriod,
            filterActivityId: state.filterActivityId,
            filterSessionIds: state.filterSessionIds,
            filterPlayerIds: state.filterPlayerIds,
            filterReportMode: state.filterReportMode,
            filterReportTemplateId: state.filterReportTemplateId,
          }
          const restored = state.kindSnapshots[kind]
          const page = restored ? null : createDefaultPage()

          return {
            filterReportKind: kind,
            kindSnapshots: { ...state.kindSnapshots, [state.filterReportKind]: kept },
            filterPeriod: restored?.filterPeriod ?? defaultPeriod(kind),
            filterActivityId: restored?.filterActivityId ?? null,
            filterSessionIds: restored?.filterSessionIds ?? [],
            filterPlayerIds: restored?.filterPlayerIds ?? [],
            filterReportMode: restored?.filterReportMode ?? state.filterReportMode,
            filterReportTemplateId: restored?.filterReportTemplateId ?? null,
            pages: restored?.pages ?? [page as Page],
            currentPageId: restored?.currentPageId ?? (page as Page).id,
            selectedItemId: null,
            headGaps: {},
            headGapRows: {},
            headGapBase: {},
          }
        })
      },

      setPeriod: (period) => {
        set({ filterPeriod: period })
      },

      setHeadGap: (itemId, px, rows = 0, baseH) => {
        set((state) => {
          if (
            (state.headGaps[itemId] ?? 0) === px &&
            (state.headGapRows[itemId] ?? 0) === rows &&
            state.headGapBase[itemId] === baseH
          ) {
            return state
          }
          const headGapBase = { ...state.headGapBase }
          if (baseH === undefined) {
            delete headGapBase[itemId]
          } else {
            headGapBase[itemId] = baseH
          }

          return {
            headGaps: { ...state.headGaps, [itemId]: px },
            headGapRows: { ...state.headGapRows, [itemId]: rows },
            headGapBase,
          }
        })
      },

      duplicateItem: (pageId, itemId) => {
        const state = get()
        const page = state.pages.find((p) => p.id === pageId)
        const item = page?.items.find((i) => i.id === itemId)
        if (!item) return

        const newItem: CanvasItem = {
          ...item,
          id: generateId(),
          x: item.x + 1,
          y: item.y + 1,
          w: snapWidth(item.w),
          // 복제본은 묶음(Session Details·Table 그룹)에서 떼어 독립 아이템으로 둔다
          options: { ...item.options, groupId: undefined },
        }

        set((state) => ({
          pages: state.pages.map((p) =>
            p.id === pageId ? { ...p, items: [...p.items, newItem] } : p,
          ),
          selectedItemId: newItem.id,
        }))
      },

      setDraggingFromPalette: (isDragging, item) => {
        set({
          isDraggingFromPalette: isDragging,
          draggedPaletteItem: item ?? null,
        })
      },

      setExporting: (isExporting, progress) => {
        set({
          isExporting,
          exportProgress: progress ?? 0,
        })
      },

      setFilterActivity: (id) => {
        set({ filterActivityId: id })
      },

      setFilterSessions: (ids) => {
        set({ filterSessionIds: ids })
      },

      setFilterPlayers: (ids) => {
        set({ filterPlayerIds: ids })
      },

      setFilterReportMode: (mode) => {
        set({ filterReportMode: mode })
      },

      setFilterReportTemplate: (id) => {
        set({ filterReportTemplateId: id })
      },

      reset: (options) => {
        const defaultPage = createDefaultPage()
        const {
          filterReportKind,
          filterPeriod,
          filterActivityId,
          filterSessionIds,
          filterPlayerIds,
          filterReportMode,
          filterReportTemplateId,
          kindSnapshots,
        } = get()
        set({
          ...initialState,
          pages: [defaultPage],
          currentPageId: defaultPage.id,
          ...(options?.keepFilters
            ? {
                kindSnapshots,
                filterReportKind,
                filterPeriod,
                filterActivityId,
                filterSessionIds,
                filterPlayerIds,
                filterReportMode,
                filterReportTemplateId,
              }
            : {}),
        })
      },
    }),
    {
      store: "pdfBuilder",
      enabled: process.env.NODE_ENV !== "production",
    },
  ),
)

export const selectCurrentPage = (state: PdfBuilderStore): Page | undefined =>
  state.pages.find((p) => p.id === state.currentPageId)

/**
 * 용지 설정만 구독한다 — 아이템이 바뀌어도 config 참조는 그대로라 재렌더가 끊긴다.
 * selectCurrentPage 는 pages.map 으로 매번 새 page 객체를 돌려주므로, config 만 필요한 곳에서
 * 쓰면 아이템 하나 움직일 때마다 모든 차트가 다시 그려진다 (일감 #197).
 */
export const selectCurrentPageConfig = (state: PdfBuilderStore): Page["config"] | undefined =>
  state.pages.find((p) => p.id === state.currentPageId)?.config

export const selectSelectedItem = (state: PdfBuilderStore): CanvasItem | undefined => {
  const currentPage = selectCurrentPage(state)

  return currentPage?.items.find((i) => i.id === state.selectedItemId)
}

export const selectCurrentPageItems = (state: PdfBuilderStore): CanvasItem[] =>
  selectCurrentPage(state)?.items ?? []

export const itemsToLayouts = (items: CanvasItem[]): LayoutItem[] =>
  items.map((item) => ({
    i: item.id,
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
    minW: item.minW,
    minH: item.minH,
    maxW: item.maxW,
    maxH: item.maxH,
  }))
